// Web Audio plumbing for a live voice session: mic capture out, model audio in. Owns every AudioContext
// and node it creates so the hook above only has to call teardown().

import {
  ASSISTANT_MIC_SAMPLE_RATE,
  ASSISTANT_PLAYBACK_SAMPLE_RATE,
  downsampleFloat32,
  float32ToInt16,
  getWorkletModuleUrl,
  int16ToFloat32,
  measureAnalyserLevel
} from './audioUtils'
import {
  ASSISTANT_MIC_CONTEXT_OPTIONS,
  ASSISTANT_MIC_PROCESSOR_NAME,
  ASSISTANT_MIC_PROCESSOR_SOURCE
} from './assistantMicWorklet'
import {
  ASSISTANT_PLAYBACK_CONTEXT_OPTIONS,
  PLAYER_FLUSH_MESSAGE,
  canRunPlayer,
  loadPlayerNode
} from './assistantPlayerWorklet'

const MIC_CHANNEL_COUNT = 1

/**
 * Fallback capture size, used only where AudioWorklet is unavailable. A ScriptProcessorNode runs its
 * callback on the MAIN thread, so it competes with React rendering: while the transcript is updating
 * on every incoming fragment, capture callbacks get delayed and the audio reaching Gemini arrives late
 * and gappy. That is the actual reason a spoken turn felt slow to get in — not the model's reply.
 */
const FALLBACK_BUFFER_SIZE = 4096

/** Small window: enough for a stable level, short enough that the indicator tracks speech. */
const LEVEL_ANALYSER_FFT_SIZE = 256

// A browser that refuses the model's rate gets its own, and plays through buffer sources, which resample.
const createPlaybackContext = (): AudioContext => {
  try {
    return new AudioContext(ASSISTANT_PLAYBACK_CONTEXT_OPTIONS)
  } catch {
    return new AudioContext()
  }
}

export type AssistantAudioPipeline = {
  /** Route a mic stream into `onChunk` as 16 kHz PCM until teardown. */
  attachMicStream: (stream: MediaStream, onChunk: (pcm: Int16Array) => void) => Promise<void>
  /** Queue model audio so consecutive chunks play back-to-back without gaps. */
  enqueuePlayback: (pcm: Int16Array) => void
  /** Barge-in: drop everything queued and reset the cursor. */
  stopPlayback: () => void
  setMuted: (muted: boolean) => void
  /**
   * Current loudness, 0–1, of the microphone and of Aria's playback. Read synchronously from an
   * analyser so an animation can sample them per frame without any React state in the loop.
   */
  getInputLevel: () => number
  getOutputLevel: () => number
  /** Stop the mic, close both contexts and release the stream. Safe to call twice. */
  teardown: () => void
}

export function createAssistantAudioPipeline(): AssistantAudioPipeline {
  let micStream: MediaStream | undefined
  let micContext: AudioContext | undefined
  let micNode: AudioNode | undefined
  let inputAnalyser: AnalyserNode | undefined
  let playbackContext: AudioContext | undefined
  let outputAnalyser: AnalyserNode | undefined
  let playbackCursor = 0
  let playbackSources: AudioBufferSourceNode[] = []
  let playerNode: AudioWorkletNode | undefined
  let isPlayerLoading = false
  let chunksAwaitingPlayer: Float32Array[] = []
  let muted = false

  // Where the player cannot run, one buffer source per chunk, each starting where the last one ends.
  const scheduleBufferSource = (context: AudioContext, samples: Float32Array) => {
    const buffer = context.createBuffer(MIC_CHANNEL_COUNT, samples.length, ASSISTANT_PLAYBACK_SAMPLE_RATE)
    buffer.getChannelData(0).set(samples)

    const source = context.createBufferSource()
    source.buffer = buffer
    source.connect(outputAnalyser ?? context.destination)

    const startAt = Math.max(context.currentTime, playbackCursor)
    source.start(startAt)
    playbackCursor = startAt + buffer.duration
    playbackSources.push(source)
    source.onended = () => {
      playbackSources = playbackSources.filter((queued) => queued !== source)
    }
  }

  const playSamples = (context: AudioContext, samples: Float32Array) => {
    if (playerNode) {
      playerNode.port.postMessage(samples, [samples.buffer])
      return
    }

    if (isPlayerLoading) {
      chunksAwaitingPlayer.push(samples)
      return
    }

    scheduleBufferSource(context, samples)
  }

  // The chunks that arrive while the player loads wait for it, and play through buffer sources if it fails.
  const startPlayer = (context: AudioContext) => {
    if (!canRunPlayer(context)) return

    isPlayerLoading = true
    void loadPlayerNode(context).then((node) => {
      const isTornDown = context !== playbackContext
      if (isTornDown) {
        node?.disconnect()
        return
      }

      node?.connect(outputAnalyser ?? context.destination)
      playerNode = node
      isPlayerLoading = false
      chunksAwaitingPlayer.forEach((samples) => playSamples(context, samples))
      chunksAwaitingPlayer = []
    })
  }

  const ensurePlaybackContext = (): AudioContext => {
    if (!playbackContext || playbackContext.state === 'closed') {
      playbackContext = createPlaybackContext()
      outputAnalyser = playbackContext.createAnalyser()
      outputAnalyser.fftSize = LEVEL_ANALYSER_FFT_SIZE
      outputAnalyser.connect(playbackContext.destination)
      // A player belongs to the context it was made in.
      playerNode = undefined
      isPlayerLoading = false
      chunksAwaitingPlayer = []
      startPlayer(playbackContext)
    }
    // Also on every use, not only at creation: a context suspended by autoplay policy or by a
    // backgrounded tab still accepts scheduled buffers, and its clock does not advance — so Aria
    // reads as speaking, the captions arrive, and nothing is ever heard.
    if (playbackContext.state === 'suspended') void playbackContext.resume().catch(() => undefined)
    return playbackContext
  }

  const stopPlayback = () => {
    playerNode?.port.postMessage(PLAYER_FLUSH_MESSAGE)
    chunksAwaitingPlayer = []
    playbackSources.forEach((source) => {
      try {
        source.stop()
      } catch {
        // already stopped
      }
    })
    playbackSources = []
    playbackCursor = playbackContext?.currentTime ?? 0
  }

  // Capture on the audio thread where it belongs. Falls back to the deprecated main-thread node only
  // where AudioWorklet is missing, since a dropped mic frame is worse than a busy main thread.
  const attachWorkletCapture = async (
    context: AudioContext,
    source: MediaStreamAudioSourceNode,
    onChunk: (pcm: Int16Array) => void
  ): Promise<boolean> => {
    if (!context.audioWorklet) return false
    try {
      await context.audioWorklet.addModule(getWorkletModuleUrl(ASSISTANT_MIC_PROCESSOR_SOURCE))
    } catch {
      return false
    }

    const worklet = new AudioWorkletNode(context, ASSISTANT_MIC_PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: MIC_CHANNEL_COUNT
    })
    worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
      if (muted) return
      onChunk(float32ToInt16(downsampleFloat32(event.data, context.sampleRate, ASSISTANT_MIC_SAMPLE_RATE)))
    }

    source.connect(worklet)
    micNode = worklet
    return true
  }

  const attachFallbackCapture = (
    context: AudioContext,
    source: MediaStreamAudioSourceNode,
    onChunk: (pcm: Int16Array) => void
  ) => {
    const processor = context.createScriptProcessor(FALLBACK_BUFFER_SIZE, MIC_CHANNEL_COUNT, MIC_CHANNEL_COUNT)
    processor.onaudioprocess = (event) => {
      if (muted) return
      const input = event.inputBuffer.getChannelData(0)
      onChunk(float32ToInt16(downsampleFloat32(input, context.sampleRate, ASSISTANT_MIC_SAMPLE_RATE)))
    }
    source.connect(processor)
    // A ScriptProcessorNode only fires while it is connected to something.
    processor.connect(context.destination)
    micNode = processor
  }

  return {
    async attachMicStream(stream, onChunk) {
      micStream = stream
      // Asking for 16 kHz lets the browser resample in native code with a proper anti-aliasing filter.
      // Decimating 48 kHz in JS folds everything above 8 kHz back into the speech band as noise, which
      // is exactly the band speech recognition depends on.
      const context = new AudioContext(ASSISTANT_MIC_CONTEXT_OPTIONS)
      micContext = context
      const source = context.createMediaStreamSource(stream)

      inputAnalyser = context.createAnalyser()
      inputAnalyser.fftSize = LEVEL_ANALYSER_FFT_SIZE
      source.connect(inputAnalyser)

      const usingWorklet = await attachWorkletCapture(context, source, onChunk)
      if (!usingWorklet) attachFallbackCapture(context, source, onChunk)
    },

    enqueuePlayback(pcm) {
      playSamples(ensurePlaybackContext(), int16ToFloat32(pcm))
    },

    stopPlayback,

    setMuted(next) {
      muted = next
    },

    getInputLevel: () => (muted ? 0 : measureAnalyserLevel(inputAnalyser)),
    getOutputLevel: () => measureAnalyserLevel(outputAnalyser),

    teardown() {
      micNode?.disconnect()
      micNode = undefined
      inputAnalyser?.disconnect()
      inputAnalyser = undefined
      void micContext?.close().catch(() => undefined)
      micContext = undefined
      micStream?.getTracks().forEach((track) => track.stop())
      micStream = undefined
      stopPlayback()
      playerNode?.disconnect()
      playerNode = undefined
      isPlayerLoading = false
      outputAnalyser?.disconnect()
      outputAnalyser = undefined
      void playbackContext?.close().catch(() => undefined)
      playbackContext = undefined
      // The cursor is a position on the clock of the context just closed. The next session builds a
      // fresh context whose clock restarts near zero, so carrying this over schedules Aria's first
      // reply as far into the future as the previous session was long — silence, with the captions
      // still arriving. This pipeline outlives a session, so it has to be reset here.
      playbackCursor = 0
    }
  }
}

/** Playback level below which nothing audible is coming out. */
const SILENT_OUTPUT_LEVEL = 0.01

/** Long enough to ride over the gaps between words, short enough not to hold the line open after. */
const QUIET_BEFORE_HANGUP_MS = 700 // 0.7 s

/** A goodbye is a sentence or two; past this something is wrong and the call ends regardless. */
const MAX_WAIT_FOR_FAREWELL_MS = 15_000 // 15 s

const HANGUP_POLL_INTERVAL_MS = 100 // 100 ms

/** Resolve once Aria has stopped speaking, so hanging up never cuts off her goodbye — her turn
 * completes long before the audio it produced has played out. */
export function waitForAriaToFinishSpeaking(getOutputLevel: () => number): Promise<void> {
  return new Promise((resolve) => {
    const startedAt = Date.now()
    let quietSince: number | undefined
    let hasSpoken = false

    const poll = setInterval(() => {
      const now = Date.now()
      const isQuiet = getOutputLevel() < SILENT_OUTPUT_LEVEL
      if (!isQuiet) hasSpoken = true
      quietSince = isQuiet ? (quietSince ?? now) : undefined

      // Silence before the first word is the audio still being decoded, not a finished goodbye: the
      // request to hang up arrives in the same turn as the farewell, ahead of any of its sound.
      const hasSettled = hasSpoken && quietSince !== undefined && now - quietSince >= QUIET_BEFORE_HANGUP_MS
      if (!hasSettled && now - startedAt < MAX_WAIT_FOR_FAREWELL_MS) return

      clearInterval(poll)
      resolve()
    }, HANGUP_POLL_INTERVAL_MS)
  })
}
